import React from "react";

import DataTable from "../../../DataTable";

import useOutputReportStore from "../../../../store/OutputReport.store";
import {
  formatLabel,
  getOutputType,
} from "../../../../utils/helpers/dataTable";

import { reportDataIndexingOverride } from "../../../../helpers";
import { dataTableCopy } from "../../../../utils/resources/dataTableCopy";
import { definitions } from "../../../../utils/resources/definitionsCopy";
import Definitions from "../../../Definitions";
import "./TransitionSummaryDataTable.scss";

const getFootnote = ({ assetType }) => {
  if (assetType === "equity") {
    return (
      <>
        Notes
        <br />
        {dataTableCopy.unlessOtherwiseStated}
        <br />* {dataTableCopy.existingPortfolioInformation}
        <br />
        ** {dataTableCopy.tsTransitionAnalysisDiscussed}
        <br />
        *** {dataTableCopy.taxRatesUsed}
        <br />
        **** {dataTableCopy.transitionTaxSavings}
      </>
    );
  }

  return (
    <>
      <h3>NOTES</h3>
      <br />
      {dataTableCopy.transitionSummaryList}
      <br />
      {dataTableCopy.unlessOtherwiseStated}
      <br />
    </>
  );
};

const TransitionSummaryDataTable = () => {
  const reportName = "Transition Summary"; // Refer to corresponding API Key
  const data = useOutputReportStore((state) => state.data);
  const types = useOutputReportStore((state) => state.types);

  const reportData = data[reportName];

  const { reportType, assetType } = getOutputType(data);
  const isEquityReport = assetType === "equity";

  const taxSavingsProperty = isEquityReport
    ? "Potential Transition Tax Savings***"
    : "Potential Transition Tax Savings [3]";

  const dataKeys = {
    1: isEquityReport
      ? "Full Liquidation of Pre-Existing Portfolio"
      : "Full Liquidation of Pre-existing Portfolio",
    2: "Transition To Remi",
  };

  const rows = [
    {
      label: "Unrealized Gains[1]",
      key: "Proposed Realized Gains",
      key_alt: "Unrealized Gains",
    },
    {
      label: "Long Term",
      key: "Long Term Proposed Realized Gains",
      key_alt: "Long Term Unrealized Gains",
    },
    {
      label: "Short Term",
      key: "Short Term Proposed Realized Gains",
      key_alt: "Short Term Unrealized Gains",
    },
    {
      label: "Unrealized Losses[1]",
      key: "Unrealized Losses [1]",
      key_alt: "Unrealized Losses [1]",
    },
    {
      label: "Long Term",
      key: "Long Term Proposed Realized Losses",
      key_alt: "Long Term Unrealized Losses",
    },
    {
      label: "Short Term",
      key: "Short Term Proposed Realized Losses",
      key_alt: "Short Term Unrealized Losses",
    },
    {
      label: "Net Realized Gains / Losses",
      key: "Net Realized Gains/Losses",
      key_alt: "",
    },
    {
      label: "No. Securities Sold (all/part)",
      key: "No. Securities Sold (all/part)",
      key_alt: "Number of Holdings",
    },
    {
      label: "No. Securities Bought",
      key: "No. Securities Bought",
      key_alt: "",
    },
    {
      label: "Proposed Turnover",
      key: "Proposed Turnover",
      key_alt: "",
      isHeader: true,
    },
    {
      label: "Buys",
      key: "Buys",
      key_alt: "",
    },
    {
      label: "Sells",
      key: "Sells",
      key_alt: "",
    },
    {
      label: "Cash Raised",
      key: "Cash Raised",
      key_alt: "Cash To Raise",
    },
    {
      label: "Taxes",
      key: "Taxes",
      key_alt: "",
      isHeader: true,
    },
    {
      label: "Long Term Tax Rate",
      key: "Long-Term Tax Rate",
      key_alt: "Long Term Tax Rate",
    },
    {
      label: "Short Term Tax Rate",
      key: "Short-Term Tax Rate",
      key_alt: "Short Term Tax Rate",
    },
    {
      label: "Transition Tax Cost",
      key: "Transition Tax Cost",
      key_alt: "",
    },
    {
      label: "Potential Transition Tax Savings[3]",
      key: taxSavingsProperty,
      key_alt: "",
    },
    {
      label: "Risk Reduction",
      key: "Risk Reduction",
      key_alt: "",
      isHeader: true,
    },
    {
      label: "Tracking Error",
      key: "Ending Tracking Error",
      key_alt: "",
    },
  ];

  const getFullLiquidationData = (reportData) => {
    const fullLiquidation = reportData[dataKeys[1]];

    console.log(`DEREK: ${JSON.stringify(fullLiquidation)}`);

    if (!fullLiquidation) return null;

    return {
      columnTitle: "Full Liquidation of Pre-Existing Portfolio",
      "Proposed Realized Gains": fullLiquidation["Unrealized Gains"],
      "Long Term Proposed Realized Gains":
        fullLiquidation["Long Term Unrealized Gains"],
      "Short Term Proposed Realized Gains":
        fullLiquidation["Short Term Unrealized Gains"],
      "Proposed Realized Losses": fullLiquidation["Unrealized Losses"],
      "Long Term Proposed Realized Losses":
        fullLiquidation["Long Term Unrealized Losses"],
      "Short Term Proposed Realized Losses":
        fullLiquidation["Short Term Unrealized Losses"],
      "Net Realized Gains/Losses": "-",
      "No. Securities Sold (all/part)": fullLiquidation["Number Of Holdings"],
      "No. Securities Bought": "-",
      "Proposed Turnover": null,
      Buys: "-",
      Sells: "-",
      "Cash Raised": fullLiquidation["Cash To Raise"],
      "Long-Term Tax Rate": fullLiquidation["Long-Term Tax Rate"],
      "Short-Term Tax Rate": fullLiquidation["Short-Term Tax Rate"],
      "Transition Tax Cost": fullLiquidation["Full Liquidation Tax Cost"],
      "Potential Transition Tax Savings": "-",
      "Beginning Tracking Error": "-",
      "Ending Tracking Error": "-",
    };
  };

  const tableColumns = Object.entries(reportData[dataKeys[2]]).map((entry) => ({
    columnTitle: entry[0],
    ...entry[1],
  }));
  const fullLiquidation = getFullLiquidationData(reportData);
  if (fullLiquidation) {
    tableColumns.unshift(fullLiquidation);
  }

  const reorderedTableColumns = reportDataIndexingOverride(
    tableColumns,
    assetType,
    reportType,
    reportName
  );

  const definitionsArgs = {
    isHeadingHidden: true,
    bodyContent: [
      {
        heading: "Explanation",
        explanation: definitions.transitionSummary.description,
      },
    ],
    isReportPage: false,
  };

  const getDefinitions = () => <Definitions {...definitionsArgs} />;

  return (
    <DataTable
      reportType={reportType}
      name="transactionSummary"
      variation="stylized"
      tableCaption={`${reportName} Data Table`}
      tableRowHeaderKey=""
      tableRowKeys={rows}
      tableColumns={reorderedTableColumns}
      tableDataTypes={types?.[reportName]}
      formatCellDataBy="row"
      footnote={getFootnote({ assetType })}
      definitions={isEquityReport ? getDefinitions() : null}
      info={
        <>
          <div>
            Initial Portfolio Value Excluding Accrued Interest:{" "}
            <b>
              {formatLabel(
                "currency",
                reportData[dataKeys[1]][
                  "Initial Portfolio Value Excluding Accrued Interest"
                ]
              )}
            </b>
          </div>
          <div>
            Cost Basis:{" "}
            <b>
              {formatLabel("currency", reportData[dataKeys[1]]["Cost Basis"])}
            </b>
          </div>
          <div>
            Number Of Holdings:{" "}
            <b>{reportData[dataKeys[1]]["Number Of Holdings"]}</b>
          </div>
        </>
      }
    />
  );
};

export default TransitionSummaryDataTable;
